import type { DocumentEmbeddingProgress } from '../types/knowledge';

export type DocumentEmbeddingProgressStreamHandlers = {
  onUpdate: (progress: DocumentEmbeddingProgress) => void;
  onComplete?: () => void;
};

function camelCaseKey(key: string): string {
  return key.replace(/_([a-z])/g, (_, character: string) =>
    character.toUpperCase(),
  );
}

export function normalizeDocumentEmbeddingProgressResponse(
  value: unknown,
): DocumentEmbeddingProgress | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  const nested =
    record.payload && typeof record.payload === 'object'
      ? record.payload
      : record.data && typeof record.data === 'object'
        ? record.data
        : value;

  if (!nested || typeof nested !== 'object') {
    return null;
  }

  const source = nested as Record<string, unknown>;
  const normalized: Record<string, unknown> = {};
  for (const [key, fieldValue] of Object.entries(source)) {
    normalized[camelCaseKey(key)] = fieldValue;
  }

  if (
    !('documentId' in normalized) &&
    !('embeddedChunks' in normalized) &&
    !('totalChunks' in normalized) &&
    !('percentage' in normalized) &&
    !('status' in normalized)
  ) {
    return null;
  }

  return normalized as DocumentEmbeddingProgress;
}

export function parseDocumentEmbeddingProgressPayload(
  payload: string,
): DocumentEmbeddingProgress | null {
  const trimmed = payload.trim();
  if (trimmed === '' || trimmed === '[DONE]') {
    return null;
  }

  try {
    return normalizeDocumentEmbeddingProgressResponse(JSON.parse(trimmed));
  } catch {
    return null;
  }
}

export function isTerminalDocumentEmbeddingProgress(
  progress: DocumentEmbeddingProgress,
): boolean {
  return progress.status === 'READY' || progress.status === 'FAILED';
}

function emitParsedProgress(
  progress: DocumentEmbeddingProgress,
  handlers: DocumentEmbeddingProgressStreamHandlers,
) {
  handlers.onUpdate(progress);
  if (isTerminalDocumentEmbeddingProgress(progress)) {
    handlers.onComplete?.();
  }
}

function extractCompleteJsonObject(
  text: string,
): { json: string; endIndex: number } | null {
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (char === '\\') {
        escaped = true;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }

    if (char === '"') {
      inString = true;
      continue;
    }

    if (char === '{') {
      depth += 1;
    } else if (char === '}') {
      depth -= 1;
      if (depth === 0) {
        return { json: text.slice(0, index + 1), endIndex: index + 1 };
      }
    }
  }

  return null;
}

/** Feed incremental stream text; returns unconsumed remainder. */
export function feedDocumentEmbeddingProgressStreamBuffer(
  buffer: string,
  handlers: DocumentEmbeddingProgressStreamHandlers,
): string {
  let rest = buffer;

  while (rest.length > 0) {
    rest = rest.trimStart();
    if (rest.length === 0) {
      break;
    }

    if (rest.startsWith(':') || rest.startsWith('event:')) {
      const lineEnd = rest.indexOf('\n');
      if (lineEnd === -1) {
        break;
      }
      rest = rest.slice(lineEnd + 1);
      continue;
    }

    if (rest.startsWith('data:')) {
      const afterPrefix = rest.slice(5).trimStart();
      const lineEnd = afterPrefix.indexOf('\n');
      const line = lineEnd === -1 ? afterPrefix : afterPrefix.slice(0, lineEnd);
      const progress = parseDocumentEmbeddingProgressPayload(line);
      if (progress) {
        emitParsedProgress(progress, handlers);
      }
      rest = lineEnd === -1 ? '' : afterPrefix.slice(lineEnd + 1);
      continue;
    }

    if (rest.startsWith('{')) {
      const extracted = extractCompleteJsonObject(rest);
      if (!extracted) {
        break;
      }

      const progress = parseDocumentEmbeddingProgressPayload(extracted.json);
      if (progress) {
        emitParsedProgress(progress, handlers);
      }
      rest = rest.slice(extracted.endIndex);
      continue;
    }

    const lineEnd = rest.indexOf('\n');
    if (lineEnd === -1) {
      break;
    }

    const progress = parseDocumentEmbeddingProgressPayload(
      rest.slice(0, lineEnd),
    );
    if (progress) {
      emitParsedProgress(progress, handlers);
    }
    rest = rest.slice(lineEnd + 1);
  }

  return rest;
}
